import React, { useEffect, useMemo, useState } from "react";
import { mountRootParcel, start } from "single-spa";
import Parcel from "single-spa-react/parcel";
import { isNativeMobileDeviceType } from "../../../../constants";
import type { ViewerDeviceRuntime } from "../../../../types";
import { ContentFallBack } from "../ContentFallBack";
import { ensureSystemJs } from "../ensureSystemJs";
import { OfflineWidgetWrapper } from "./OfflineWidgetWrapper";
import type { WidgetConfigFields } from "../types";

// single-spa requires start() before mountRootParcel can be used in a standalone
// (non-routing) context. Safe to call multiple times — subsequent calls are no-ops.
start({ urlRerouteOnly: true });

type SpaParcelBlockProps = {
  widgetConfig: WidgetConfigFields | undefined;
  device: ViewerDeviceRuntime;
};

function useParcelConfig(widgetConfig: WidgetConfigFields | undefined) {
  return useMemo(() => {
    if (!widgetConfig?.format || !widgetConfig?.url) {
      return null;
    }
    const { format, url } = widgetConfig;
    if (format === "es6") {
      return () => import(/* webpackIgnore: true */ url);
    }
    return async () => {
      await ensureSystemJs();
      const sys = (window as unknown as { System?: { import: (m: string) => Promise<unknown> } })
        .System;
      if (!sys?.import) {
        throw new Error("System.import is not available");
      }
      return sys.import(/* webpackIgnore: true */ url);
    };
  }, [widgetConfig]);
}

const SpaParcelBlockInner: React.FC<SpaParcelBlockProps> = ({ widgetConfig, device }) => {
  const parcelConfig = useParcelConfig(widgetConfig);
  const [systemReady, setSystemReady] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    if (!widgetConfig?.url || widgetConfig.format === "es6") {
      return true;
    }
    return Boolean((window as unknown as { System?: unknown }).System);
  });

  const needsSystem =
    Boolean(widgetConfig?.url) && widgetConfig?.format != null && widgetConfig.format !== "es6";

  useEffect(() => {
    if (!needsSystem) {
      setSystemReady(true);
      return;
    }
    let cancelled = false;
    ensureSystemJs()
      .then(() => {
        if (!cancelled) {
          setSystemReady(true);
        }
      })
      .catch((e) => {
        console.error("[SpaParcelBlock] SystemJS load failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [needsSystem]);

  if (needsSystem && !systemReady) {
    return <div>Loading…</div>;
  }

  if (!widgetConfig) {
    return <ContentFallBack parcelConfig={null} device={device} children={null} />;
  }

  if (!parcelConfig) {
    return <ContentFallBack parcelConfig={null} device={device} children={null} />;
  }

  return (
    <ContentFallBack parcelConfig={parcelConfig} device={device}>
      {isNativeMobileDeviceType(device.deviceType) && !device.isOnline() ? (
        <OfflineWidgetWrapper />
      ) : (
        <Parcel
          key={widgetConfig.url}
          mountParcel={mountRootParcel}
          config={parcelConfig}
          wrapWith="section"
          block={false}
          wrapStyle={{
            width: "100%",
            ...((widgetConfig.style || {}) as React.CSSProperties),
          }}
          handleError={(error: Error) =>
            console.error(`[SpaParcelBlock] Parcel error for ${widgetConfig.url}:`, error)
          }
        />
      )}
    </ContentFallBack>
  );
};

/**
 * Avoid remounting the parcel on every parent render when the widget object identity is stable.
 */
export const SpaParcelBlock = React.memo(
  SpaParcelBlockInner,
  (prev, next) => prev.widgetConfig === next.widgetConfig && prev.device === next.device,
);
